import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Tooltip, Snackbar, Alert, Stack, MenuItem, TablePagination, InputAdornment
} from '@mui/material';
import { Edit, Delete, Person, Email, Security, Badge, CheckCircle, Cancel } from '@mui/icons-material';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../services/auth/AuthContext';
import api from '../services/api';
import SearchIcon from '@mui/icons-material/Search';
import { deepPurple } from '@mui/material/colors';
import BarChartIcon from '@mui/icons-material/BarChart';

const UserStats = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isEmployee = user?.role === 'student employee';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/users');
      setUsers(res.data);
      } catch (err) {
      setError('Failed to fetch users');
      } finally {
        setLoading(false);
      }
    };

  const handleEdit = (user) => {
    let details = {};
    try {
      details = JSON.parse(user.user_details || '{}');
    } catch {}
    setEditUser({
      ...user,
      ...details,
      status: details.status || '',
      name: details.name || '',
      student_id: details.student_id || '',
      program: details.program || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditChange = (e) => {
    setEditUser({ ...editUser, [e.target.name]: e.target.value });
  };

  const handleEditSave = async () => {
    try {
      const user_details = JSON.stringify({
        name: editUser.name,
        student_id: editUser.student_id,
        program: editUser.program,
        status: editUser.status
      });
      await api.put(`/users/${editUser.user_id}`, {
        email: editUser.email,
        role: editUser.role,
        user_details
      });
      setSuccess('User updated successfully');
      setEditDialogOpen(false);
      fetchUsers();
    } catch (err) {
      setError('Failed to update user');
    }
  };

  const handleDelete = (user_id) => {
    setDeleteUserId(user_id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/users/${deleteUserId}`);
      setSuccess('User deleted successfully');
      setDeleteDialogOpen(false);
      fetchUsers();
    } catch (err) {
      setError('Failed to delete user');
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredUsers = users.filter(u => {
    let details = {};
    try { details = JSON.parse(u.user_details || '{}'); } catch {}
    return (
      (details.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (details.student_id || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.role || '').toLowerCase().includes(search.toLowerCase()) ||
      (details.status || '').toLowerCase().includes(search.toLowerCase())
    );
  });

  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      <Container maxWidth="lg" sx={{ pt: 10, mt: 4, mb: 6, flex: 1, pb: 8 }}>
        <Paper elevation={3} sx={{ borderRadius: 2, p: 4 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: deepPurple[800], mb: 4, display: 'flex', alignItems: 'center' }}>
              <BarChartIcon sx={{ mr: 2, fontSize: 38, color: deepPurple[600] }} />
              User Statistics
            </Typography>
            <TextField
              placeholder="Search by name, email, student ID, role, or status"
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
              size="small"
              fullWidth
            />
          </Box>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: deepPurple[50] }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}><Badge sx={{ mr: 1 }} />ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}><Person sx={{ mr: 1 }} />Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Student ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}><Security sx={{ mr: 1 }} />Role</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}><Email sx={{ mr: 1 }} />Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.map(u => {
                  let details = {};
                  try {
                    details = JSON.parse(u.user_details || '{}');
                  } catch {}
                  return (
                    <TableRow key={u.user_id}>
                      <TableCell>{u.user_id}</TableCell>
                      <TableCell>{details.name || ''}</TableCell>
                      <TableCell>{details.student_id || ''}</TableCell>
                      <TableCell>{u.role}</TableCell>
                      <TableCell>{details.status || ''}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {(isAdmin || isEmployee) && (u.role === 'user' || isAdmin) && (
                            <Tooltip title="Edit">
                              <IconButton color="primary" onClick={() => handleEdit(u)}>
                                <Edit />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isAdmin && (
                            <Tooltip title="Delete">
                              <IconButton color="error" onClick={() => handleDelete(u.user_id)}>
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      </Container>
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth label="Name" name="name" value={editUser?.name || ''} onChange={handleEditChange} sx={{ mb: 2 }} />
          <TextField fullWidth label="Student ID" name="student_id" value={editUser?.student_id || ''} onChange={handleEditChange} sx={{ mb: 2 }} />
          <TextField fullWidth label="Program" name="program" value={editUser?.program || ''} onChange={handleEditChange} sx={{ mb: 2 }} />
          <TextField
            select
            fullWidth
            label="Status"
            name="status"
            value={editUser?.status || ''}
            onChange={handleEditChange}
            sx={{ mb: 2 }}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <TextField fullWidth label="Email" name="email" value={editUser?.email || ''} onChange={handleEditChange} sx={{ mb: 2 }} />
          <TextField
            select
            fullWidth
            label="Role"
            name="role"
            value={editUser?.role || ''}
            onChange={handleEditChange}
            sx={{ mb: 2 }}
          >
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="student employee">Student Employee</MenuItem>
            <MenuItem value="user">Rider</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} startIcon={<Cancel />}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} startIcon={<CheckCircle />}>Save</Button>
        </DialogActions>
      </Dialog>
      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this user?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} startIcon={<Cancel />}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete} startIcon={<Delete />}>Delete</Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar for feedback */}
      <Snackbar open={!!error} autoHideDuration={2500} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={2500} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" sx={{ width: '100%' }}>{success}</Alert>
      </Snackbar>
      <Footer />
    </Box>
  );
};

export default UserStats;